import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
  const [showPassword, setShowPassword] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      alert("Les mots de passe ne correspondent pas.");
      return;
    }
    
    if (password.length < 6) {
      alert("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }

    setStatus('loading');
    try {
      // Utilisation de l'URL relative comme dans Login.jsx
      const response = await fetch('/api/auth/reset-password', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword: password })
      });

      if (response.ok) {
        setStatus('success');
        setTimeout(() => navigate('/login'), 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("Erreur API:", errorData);
        setStatus('error');
      }
    } catch (error) {
      console.error("Détails de l'erreur technique :", error);
      setStatus('error');
    }
  };

  // Vérification de la présence du token
  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans text-left">
        <div className="max-w-md w-full bg-white p-8 rounded-3xl shadow-xl text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Lien invalide</h2>
          <p className="text-slate-500 mb-4">Aucun token de réinitialisation trouvé.</p>
          <Link to="/forgot-password" className="text-cyan-600 hover:underline">
            Faire une nouvelle demande
          </Link>
          <div className="mt-6 pt-4 border-t">
            <Link to="/login" className="text-sm text-slate-400 hover:text-cyan-600 transition">
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans text-left">
      <div className="max-w-md w-full bg-white p-8 rounded-3xl shadow-xl">
        <h2 className="text-2xl font-bold text-slate-800 mb-6">Nouveau mot de passe</h2>

        {status === 'success' ? (
          <div className="bg-green-50 text-green-700 p-4 rounded-xl border border-green-200 animate-in zoom-in text-center">
            <div className="text-4xl mb-2">✅</div>
            Mot de passe réinitialisé avec succès !<br />
            Redirection vers la page de connexion...
          </div>
        ) : (
          <form onSubmit={handleReset} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase text-slate-400 mb-1">Nouveau mot de passe</label>
              <div className="relative">
                <input 
                  type={showPassword ? "text" : "password"}
                  required 
                  className="w-full border rounded-xl p-3 pr-12 focus:ring-2 focus:ring-cyan-500 outline-none transition"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  disabled={status === 'loading'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-slate-400 mb-1">Confirmer le mot de passe</label>
              <input 
                type={showPassword ? "text" : "password"}
                required 
                className="w-full border rounded-xl p-3 focus:ring-2 focus:ring-cyan-500 outline-none transition"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                disabled={status === 'loading'}
              />
            </div>

            {status === 'error' && (
              <p className="text-red-500 text-xs font-bold italic bg-red-50 p-2 rounded-lg">
                ⚠️ Lien invalide ou expiré. Veuillez refaire une demande.
              </p>
            )}

            <button 
              disabled={status === 'loading'}
              className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === 'loading' ? 'Mise à jour...' : 'Réinitialiser le mot de passe'}
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-slate-400 hover:text-cyan-600 transition">
            Annuler et retourner au login
          </Link>
        </div>
      </div>
    </div>
  );
}